import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, Flex, SimpleGrid, Alert } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { useDebouncedCallback } from "@mantine/hooks";
import { IconPlus, IconTrash, IconAlertCircle } from "@tabler/icons-react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import {
  Button,
  DatePicker,
  DECIMAL_PROPS,
  MonthPicker,
  NumberInput,
  PhoneNumberInput,
  PostalAndCity,
  RadioGroup,
  Select,
  StreetAndHouseNumber,
  Text,
  TextInput,
  YesNoRadioButton,
} from "components";
import SectionTitle from "pages/clients/view/components/section-title";
import {
  BankCreditOptionsResponse,
  BankCreditRequestEmploymentResponse,
  BankCreditRequestForm,
  BankCreditResponse,
  TravelingType,
} from "types";
import { AppUtils, DateUtils, DistanceUtils, IbanUtils } from "utils";

import { EmploymentPreviousEmployer } from "./employment-previous-employer";

interface BankCreditRequestEmploymentProps {
  credit: BankCreditResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  options: BankCreditOptionsResponse;
  partner: boolean;
  hasAdditionalIncome: boolean;
  onAdditionalIncomeChange: (hasIncome: boolean, isPartner: boolean) => void;
  onRegisterRevalidation?: (
    revalidateFn: (
      newBirthDate?: Date | undefined | null,
      newVisaFrom?: Date | undefined | null,
    ) => void,
  ) => void;
  readOnly?: boolean;
}

type EmploymentFormItem = Omit<
  BankCreditRequestEmploymentResponse,
  "id" | "creditRequestId" | "numOfSalariesPerYear"
> & {
  numOfSalariesPerYear?: string;
};

const tempEmploymentObject: Partial<EmploymentFormItem> = {};

const propertyName = "employments";
const shortRideTravelingTypes: TravelingType[] = [
  "BICYCLE", // Only valid up to 10km
  "E_BIKE_25KM", // Only valid up to 10km
  "E_BIKE_45KM", // Only valid up to 10km
];
const WALKING_DISTANCE_LIMIT_KM = 2;
const SHORT_RIDE_DISTANCE_LIMIT_KM = 10;

const BankCreditRequestEmployment = ({
  credit,
  form,
  options,
  partner,
  readOnly = false,
  onAdditionalIncomeChange,
  hasAdditionalIncome,
  onRegisterRevalidation,
}: BankCreditRequestEmploymentProps) => {
  const { t } = useTranslation();
  const isFirstRender = useRef(true);
  const [localVisibleFields, setLocalVisibleFields] = useState<string[]>(
    AppUtils.getVisibleEmploymentFields(credit[propertyName], true, credit),
  );
  const [contractStartDateErrors, setContractStartDateErrors] = useState<
    Record<number, string | undefined>
  >({});
  const [employmentTypeErrors, setEmploymentTypeErrors] = useState<
    Record<number, string | undefined>
  >({});
  const [calculatingDistance, setCalculatingDistance] = useState<Record<number, boolean>>({});
  const [distanceError, setDistanceError] = useState<Record<number, string | undefined>>({});

  const quellensteuerOptions = ["YES", "NO", "NOT_VISIBLE"].map((value) => ({
    value,
    label: t(`bankCredit.quellensteuerMatchesTariff.options.${value}`),
  }));

  const salaryStructureOptions = ["FIXED", "HOURLY"].map((value) => ({
    value,
    label: t(`bankCredit.salaryStructure.options.${value}`),
  }));

  const bonusTypeOptions = options["bonusType"].map((el) => ({
    value: el,
    label: t(`bankCredit.bonusType.options.${el}`),
  }));

  const numOfSalariesPerYearOptions = [12, 13].map((value) => ({
    value: value.toString(),
    label: value.toString(),
  }));

  const travelingToWorkBnOptions = options["travelingTypeBN"].map((el) => ({
    value: el,
    label: t(`bankCredit.travelingToWork.options.${el}`),
  }));

  const travelingToWorkCbOptions = Array.from(
    new Set<TravelingType>([...options["travelingTypeCB"], "NO_COMMUTING"]),
  ).map(
    (el) => ({
      value: el,
      label: t(`bankCredit.travelingToWork.options.${el}`),
    }),
  );

  const bankOptions = options["bank"].map((el) => ({
    value: el,
    label: t(`credit.bank.options.${el}`),
  }));

  const getEmploymentSectionTitle = (index: number): string => {
    const isMainEmployment = index === 0;

    if (partner) {
      return isMainEmployment
        ? t("bankCredit.employment.mainPartner")
        : t("bankCredit.employment.additionalPartner");
    }

    return isMainEmployment ? t("credit.employment.title") : t("bankCredit.employment.additional");
  };

  const getChildrenCounts = () => {
    const children = form.getValues().children || [];
    let youngerCount = 0;
    let olderCount = 0;

    children.forEach((child) => {
      if (child.birthdate) {
        const age = DateUtils.getAgeFromBirthDate(child.birthdate);
        if (age < 15) {
          youngerCount++;
        } else {
          olderCount++;
        }
      }
    });

    return { younger: youngerCount, older: olderCount };
  };

  const additionalIncomeLabel = useMemo(
    () =>
      partner
        ? t("credit.income.hasAdditionalIncome.labelPartner")
        : t("credit.income.hasAdditionalIncome.label"),
    [partner, t],
  );
  const additionalEmploymentLabel = useMemo(
    () =>
      partner
        ? t("bankCredit.hasAdditionalEmployment.labelPartner")
        : t("bankCredit.hasAdditionalEmployment.label"),
    [partner, t],
  );
  const [hasAdditionalEmployment, setHasAdditionalEmployment] = useState(() => {
    const employments = credit.employments || [];
    return employments.filter((employment) => employment.partner === partner).length > 1;
  });

  const formValues = form.getValues();
  const visaType = formValues.visaType;
  const partnerVisaType = formValues.partnerVisaType;
  const partnerIncludeInApplication = formValues.partnerIncludeInApplication;
  const childrenCount = formValues.children?.length;

  const quellensteuerTariff = useMemo(
    () =>
      AppUtils.calculateQuellensteuerTariffCode({
        partnerIncluded: partnerIncludeInApplication === true,
        childrenCount: childrenCount ?? 0,
      }),
    [partnerIncludeInApplication, childrenCount],
  );

  const getInitialData = useCallback(() => {
    const formValues = credit[propertyName] as Partial<EmploymentFormItem>[] | undefined;
    let array: Partial<EmploymentFormItem>[] = [];
    if (!formValues || formValues.length === 0) {
      array.push(tempEmploymentObject);
    } else {
      const filtered = formValues.filter((el) => el.partner === partner);
      if (filtered.length > 0) {
        array = filtered.map((el) => ({
          ...el,
          contractStartDate: el.contractStartDate ? new Date(el.contractStartDate) : undefined,
          contractEndDate: el.contractEndDate ? new Date(el.contractEndDate) : undefined,
          previousEmployerStartDate: el.previousEmployerStartDate
            ? new Date(el.previousEmployerStartDate)
            : undefined,
          employerLoanPaidOut: el.employerLoanPaidOut
            ? new Date(el.employerLoanPaidOut)
            : undefined,
          sickLeaveReturnDate: el.sickLeaveReturnDate
            ? new Date(el.sickLeaveReturnDate)
            : undefined,
          numOfSalariesPerYear: el.numOfSalariesPerYear
            ? el.numOfSalariesPerYear.toString()
            : undefined,
          iban: el.iban ? IbanUtils.formatIban(el.iban) : undefined,
        }));
      } else {
        array.push(tempEmploymentObject);
      }
    }
    return array;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner]);

  const employmentForm = useForm({
    mode: "uncontrolled",
    initialValues: getInitialData() as Partial<EmploymentFormItem>[],
    onValuesChange(values, previous) {
      updateCredit(values, previous);
    },
  });

  /**
   * Determines the number of salaries per year based on professional situation
   * Rules:
   * 1. Self-employed (SELF_EMPLOYED_LUMP_SUM, SELF_EMPLOYED_AG) → always 12 salaries
   * 2. Pensioners (PENSION, DISABILITY_PENSION) → always 13 salaries (from 2026 onwards)
   * 3. Employed (EMPLOYED, AGENCY) → agent chooses manually (returns undefined)
   */
  const getNumOfSalariesPerYear = useCallback(
    (professionalSituation?: string): string | undefined => {
      if (!professionalSituation) return undefined;

      if (
        professionalSituation === "SELF_EMPLOYED_LUMP_SUM" ||
        professionalSituation === "SELF_EMPLOYED_AG"
      ) {
        return "12";
      }

      if (professionalSituation === "PENSION" || professionalSituation === "DISABILITY_PENSION") {
        return "13";
      }

      return undefined;
    },
    [],
  );

  const validateContractStartDate = useCallback(
    ({
      value,
      index,
      overrideBirthDate,
      overrideVisaFrom,
      overrideProfessionalSituation,
    }: {
      value: Date | undefined;
      index: number;
      overrideBirthDate?: Date | undefined | null;
      overrideVisaFrom?: Date | undefined | null;
      overrideProfessionalSituation?: string | undefined;
    }) => {
      if (partner) {
        employmentForm.clearFieldError(`${index}.contractStartDate`);
        setContractStartDateErrors((prev) => ({ ...prev, [index]: undefined }));
        return;
      }

      if (!value) {
        employmentForm.clearFieldError(`${index}.contractStartDate`);
        setContractStartDateErrors((prev) => ({ ...prev, [index]: undefined }));
        return;
      }

      const parentFormValues = form.getValues();
      // Use override if provided (not null), otherwise use form value
      const birthDate =
        overrideBirthDate !== null && overrideBirthDate !== undefined
          ? overrideBirthDate
          : parentFormValues.birthDate;
      const visaFrom =
        overrideVisaFrom !== null && overrideVisaFrom !== undefined
          ? overrideVisaFrom
          : parentFormValues.visaFrom;

      if (birthDate) {
        const minDate = moment(birthDate).add(18, "years").toDate();
        if (value < minDate) {
          const errorMsg = t("bankCredit.validation.contractStartDate.beforeAge18");
          employmentForm.setFieldError(`${index}.contractStartDate`, errorMsg);
          setContractStartDateErrors((prev) => ({ ...prev, [index]: errorMsg }));
          return;
        }
      }

      if (visaFrom && value < visaFrom) {
        const errorMsg = t("bankCredit.validation.contractStartDate.beforeVisaDate");
        employmentForm.setFieldError(`${index}.contractStartDate`, errorMsg);
        setContractStartDateErrors((prev) => ({ ...prev, [index]: errorMsg }));
        return;
      }

      const employments = employmentForm.getValues();
      const employment = (employments as any)[index];
      const professionalSituation =
        overrideProfessionalSituation ?? employment?.professionalSituation;

      if (professionalSituation === "AGENCY") {
        const sixMonthsAgo = moment().subtract(6, "months").toDate();
        if (value > sixMonthsAgo) {
          const errorMsg = t("bankCredit.validation.contractStartDate.lessThan6Months");
          employmentForm.setFieldError(`${index}.contractStartDate`, errorMsg);
          setContractStartDateErrors((prev) => ({ ...prev, [index]: errorMsg }));
          return;
        }
      }

      employmentForm.clearFieldError(`${index}.contractStartDate`);
      setContractStartDateErrors((prev) => ({ ...prev, [index]: undefined }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partner, t],
  );

  const handleProfessionalSituationChange = useCallback(
    (value: string | undefined, index: number) => {
      employmentForm.setFieldValue(`${index}.professionalSituation`, value);
      const numOfSalaries = getNumOfSalariesPerYear(value);
      employmentForm.setFieldValue(`${index}.numOfSalariesPerYear`, numOfSalaries);

      const employment = employmentForm.getValues()[index];
      if (employment?.contractStartDate) {
        validateContractStartDate({
          value: employment.contractStartDate,
          index,
          overrideProfessionalSituation: value,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validateContractStartDate, getNumOfSalariesPerYear],
  );

  const validateEmploymentType = useCallback(
    (value: string | undefined, index: number) => {
      if (partner) {
        employmentForm.clearFieldError(`${index}.employmentType`);
        setEmploymentTypeErrors((prev) => ({ ...prev, [index]: undefined }));
        return;
      }

      if (value === "DEFINED") {
        const errorMsg = t("bankCredit.validation.employmentType.defined");
        employmentForm.setFieldError(`${index}.employmentType`, errorMsg);
        setEmploymentTypeErrors((prev) => ({ ...prev, [index]: errorMsg }));
        return;
      }

      employmentForm.clearFieldError(`${index}.employmentType`);
      setEmploymentTypeErrors((prev) => ({ ...prev, [index]: undefined }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partner, t],
  );

  const handleEmploymentTypeChange = useCallback(
    (value: string | undefined, index: number) => {
      employmentForm.setFieldValue(`${index}.employmentType`, value);
      validateEmploymentType(value, index);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validateEmploymentType],
  );

  const handleContractStartDateChange = useCallback(
    (value: Date | undefined, index: number) => {
      employmentForm.setFieldValue(`${index}.contractStartDate`, value);
      validateContractStartDate({ value, index });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validateContractStartDate],
  );

  const calculateTravelExpenseBN = useCallback(
    (distanceKm: number, travelType?: TravelingType): number => {
      if (!travelType) return 0;

      switch (travelType) {
        case "WALKING":
          return 0;

        case "E_BIKE_25KM":
          return distanceKm <= SHORT_RIDE_DISTANCE_LIMIT_KM ? 15 : 0;

        case "E_BIKE_45KM":
          return distanceKm <= SHORT_RIDE_DISTANCE_LIMIT_KM ? 55 : 0;

        case "MOTORCYCLE":
          return 55;

        case "CAR_NO_PROFESSIONAL_QUALIFICATION":
          if (distanceKm <= SHORT_RIDE_DISTANCE_LIMIT_KM) {
            return distanceKm * 22;
          } else {
            return 190;
          }

        case "CAR_PROFESSIONAL_QUALIFICATION":
          return 0;

        case "PUBLIC_TRANSPORTATION":
        case "NO_COMMUTING":
          return 0;

        default:
          return 0;
      }
    },
    [],
  );

  const calculateTravelExpenseCB = useCallback(
    (distanceKm: number, travelType?: TravelingType): number => {
      switch (travelType) {
        case "CAR":
        case "CAR_NO_PROFESSIONAL_QUALIFICATION":
          return distanceKm * 14 + 100;

        case "BICYCLE":
          return 30;

        case "SCUTTER":
          return 50;

        case "MOTORCYCLE":
          return 75;

        case "PUBLIC_TRANSPORTATION":
          return distanceKm <= 10 ? 100 : distanceKm <= 30 ? 215 : 315;

        case "WALKING":
        case "NO_COMMUTING":
        default:
          return 0;
      }
    },
    [],
  );

  const recalculateTravelExpenses = useCallback(
    (
      index: number,
      overrideTravelType?: TravelingType | null,
      overrideField?: "travelingToWorkBn" | "travelingToWorkCb",
    ) => {
      const employment = employmentForm.getValues()[index];
      const distance = employment?.travelingDistance;
      const travelTypeBn = (overrideField === "travelingToWorkBn"
        ? overrideTravelType
        : employment?.travelingToWorkBn) as TravelingType | undefined;
      const travelTypeCb = (overrideField === "travelingToWorkCb"
        ? overrideTravelType
        : employment?.travelingToWorkCb) as TravelingType | undefined;

      if (!distance || distance <= 0) {
        employmentForm.setFieldValue(`${index}.travelExpensesBn`, undefined);
        employmentForm.setFieldValue(
          `${index}.travelExpensesCb`,
          travelTypeCb === "NO_COMMUTING" ? 0 : undefined,
        );
        return;
      }

      const travelExpensesBn = calculateTravelExpenseBN(distance, travelTypeBn);
      const travelExpensesCb = calculateTravelExpenseCB(distance, travelTypeCb);

      employmentForm.setFieldValue(`${index}.travelExpensesBn`, travelExpensesBn);
      employmentForm.setFieldValue(`${index}.travelExpensesCb`, travelExpensesCb);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calculateTravelExpenseBN, calculateTravelExpenseCB],
  );

  const handleTravelTypeChange = useCallback(
    (
      value: TravelingType | null,
      index: number,
      field: "travelingToWorkBn" | "travelingToWorkCb",
    ) => {
      employmentForm.setFieldValue(`${index}.${field}`, value);

      recalculateTravelExpenses(index, value, field);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recalculateTravelExpenses],
  );

  const getFilteredTravelOptions = useCallback(
    (index: number, options: Array<{ value: TravelingType; label: string }>) => {
      const employment = employmentForm.getValues()[index];
      const distance = employment?.travelingDistance;

      if (!distance || distance <= 0) {
        return options;
      }

      return options.map((opt) => ({
        ...opt,
        disabled:
          (opt.value === "WALKING" && distance > WALKING_DISTANCE_LIMIT_KM) ||
          (shortRideTravelingTypes.includes(opt.value) && distance > SHORT_RIDE_DISTANCE_LIMIT_KM),
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleCalculateDistance = useCallback(
    async (index: number) => {
      setCalculatingDistance((prev) => ({ ...prev, [index]: true }));
      setDistanceError((prev) => ({ ...prev, [index]: undefined }));

      try {
        const employment = employmentForm.getValues()[index];
        const mainFormValues = form.getValues();

        const homeAddress = DistanceUtils.buildAddressString(
          mainFormValues.street,
          mainFormValues.houseNumber,
          mainFormValues.postal,
          mainFormValues.city,
        );

        const workAddress = DistanceUtils.buildAddressString(
          employment?.employerStreet,
          employment?.employerHouseNumber,
          employment?.employerPostal,
          employment?.employerCity,
        );

        if (!homeAddress || homeAddress.length < 5) {
          throw new Error(t("bankCredit.distance.error.invalidHomeAddress"));
        }

        if (!workAddress || workAddress.length < 5) {
          throw new Error(t("bankCredit.distance.error.invalidWorkAddress"));
        }

        const result = await DistanceUtils.calculateCommuteDistance(homeAddress, workAddress);

        const roundedDistance = Math.round(result.estimatedRoadKm * 100) / 100;

        employmentForm.setFieldValue(`${index}.travelingDistance`, roundedDistance);

        const isWalkingInvalid = roundedDistance > WALKING_DISTANCE_LIMIT_KM;
        const areShortRideTypesInvalid = roundedDistance > SHORT_RIDE_DISTANCE_LIMIT_KM;

        if (employment?.travelingToWorkBn === "WALKING" && isWalkingInvalid) {
          employmentForm.setFieldValue(`${index}.travelingToWorkBn`, undefined);
        }
        if (employment?.travelingToWorkCb === "WALKING" && isWalkingInvalid) {
          employmentForm.setFieldValue(`${index}.travelingToWorkCb`, undefined);
        }

        if (
          areShortRideTypesInvalid &&
          employment?.travelingToWorkBn &&
          shortRideTravelingTypes.includes(employment.travelingToWorkBn)
        ) {
          employmentForm.setFieldValue(`${index}.travelingToWorkBn`, undefined);
        }
        if (
          areShortRideTypesInvalid &&
          employment?.travelingToWorkCb &&
          shortRideTravelingTypes.includes(employment.travelingToWorkCb)
        ) {
          employmentForm.setFieldValue(`${index}.travelingToWorkCb`, undefined);
        }

        recalculateTravelExpenses(index);
      } catch (error) {
        console.error("[Distance] Calculation error:", error);
        let errorMessage = t("bankCredit.distance.error.calculationFailed");

        if (error instanceof Error) {
          if (error.message === DistanceUtils.DISTANCE_ERROR_CODES.HOME_ADDRESS_NOT_FOUND) {
            errorMessage = t("bankCredit.distance.error.homeAddressNotFound");
          } else if (error.message === DistanceUtils.DISTANCE_ERROR_CODES.WORK_ADDRESS_NOT_FOUND) {
            errorMessage = t("bankCredit.distance.error.workAddressNotFound");
          } else {
            errorMessage = error.message;
          }
        }

        setDistanceError((prev) => ({ ...prev, [index]: errorMessage }));
      } finally {
        setCalculatingDistance((prev) => ({ ...prev, [index]: false }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, calculateTravelExpenseBN, calculateTravelExpenseCB],
  );

  const validateIbanAndSetBank = useCallback(
    (index: number, ibanValue: string, clearErrorFirst = false) => {
      if (clearErrorFirst) {
        employmentForm.clearFieldError(`${index}.iban`);
      }

      const cleaned = ibanValue.replace(/\s/g, "");

      if (cleaned.length === 21) {
        const bankResult = IbanUtils.extractBankFromIban(cleaned);
        if (bankResult.success && bankResult.bank) {
          employmentForm.setFieldValue(`${index}.bank`, bankResult.bank);
        } else {
          const errorMessage = bankResult.error ?? t("bankCredit.iban.error");
          employmentForm.setFieldError(`${index}.iban`, errorMessage);
          if (employmentForm.getValues()[index]?.bank !== undefined) {
            employmentForm.setFieldValue(`${index}.bank`, undefined);
          }
        }
      } else if (cleaned.length > 0 && cleaned.length < 21) {
        if (employmentForm.getValues()[index]?.bank !== undefined) {
          employmentForm.setFieldValue(`${index}.bank`, undefined);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  const handleIbanChange = (index: number, value: string) => {
    const formatted = IbanUtils.formatIban(value);
    employmentForm.setFieldValue(`${index}.iban`, formatted);
    validateIbanAndSetBank(index, formatted, true);
  };

  // Register revalidation function with parent (only for main user, not partner)
  useEffect(() => {
    if (partner || !onRegisterRevalidation) return;

    const revalidateAllEmployments = (
      newBirthDate?: Date | undefined | null,
      newVisaFrom?: Date | undefined | null,
    ) => {
      const employments = employmentForm.getValues();

      // Mantine form returns an object with numeric keys, not an array
      if (employments && typeof employments === "object") {
        Object.keys(employments).forEach((key) => {
          const index = Number(key);
          const employment = (employments as any)[index];
          if (employment && employment.contractStartDate) {
            validateContractStartDate({
              value: employment.contractStartDate,
              index,
              overrideBirthDate: newBirthDate,
              overrideVisaFrom: newVisaFrom,
            });
          }
        });
      }
    };

    onRegisterRevalidation(revalidateAllEmployments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner, onRegisterRevalidation]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const currentCredit = form.getValues() as BankCreditResponse;
    const employments = employmentForm.getValues();
    const newVisibleFields = AppUtils.getVisibleEmploymentFields(employments, true, currentCredit);

    if (employments && typeof employments === "object") {
      Object.keys(employments).forEach((key) => {
        const index = Number(key);
        const fieldName = `employments.${index}.quellensteuerMatchesTariff`;

        if (!newVisibleFields.includes(fieldName)) {
          const employment = (employments as any)[index];
          if (employment?.quellensteuerMatchesTariff) {
            employmentForm.setFieldValue(`${index}.quellensteuerMatchesTariff`, undefined);
          }
        }
      });
    }

    setLocalVisibleFields(newVisibleFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visaType, partnerVisaType, partnerIncludeInApplication]);

  // Validate all employments on mount and auto-set numOfSalariesPerYear if needed
  useEffect(() => {
    const employments = employmentForm.getValues();
    if (employments && typeof employments === "object") {
      Object.keys(employments).forEach((key) => {
        const index = Number(key);
        const employment = (employments as any)[index];
        if (employment) {
          if (employment.professionalSituation) {
            const expectedNumOfSalaries = getNumOfSalariesPerYear(employment.professionalSituation);
            if (
              expectedNumOfSalaries !== undefined &&
              employment.numOfSalariesPerYear !== expectedNumOfSalaries
            ) {
              employmentForm.setFieldValue(`${index}.numOfSalariesPerYear`, expectedNumOfSalaries);
            }
          }

          if (employment.contractStartDate) {
            validateContractStartDate({ value: employment.contractStartDate, index });
          }
          if (employment.employmentType) {
            validateEmploymentType(employment.employmentType, index);
          }
          if (employment.iban) {
            validateIbanAndSetBank(index, employment.iban, false);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCredit = useDebouncedCallback(async (values: any, previous: any) => {
    const currentCredit = form.getValues() as BankCreditResponse;
    const prevVisibleFields = AppUtils.getVisibleEmploymentFields(previous, true, currentCredit);
    const newVisibleFields = AppUtils.getVisibleEmploymentFields(values, true, currentCredit);
    AppUtils.clearArrayFieldsWhenUpdating(values, previous, newVisibleFields);
    AppUtils.updateArrayFieldsWhenVisibilityChanges(
      prevVisibleFields,
      newVisibleFields,
      values,
      employmentForm,
    );

    const formValues = form.getValues()[propertyName] as Partial<EmploymentFormItem>[] | undefined;
    const tempArray: Partial<EmploymentFormItem>[] = formValues
      ? formValues.filter((el) => el.partner !== partner)
      : [];
    Object.keys(values).forEach((key) => {
      const item: Partial<EmploymentFormItem> = { ...values[key] };

      // Check if employment has any meaningful data (excluding metadata field partner)
      const excludedFields = ["partner"];
      const isNotEmpty = Object.keys(item).some(
        (field) =>
          !excludedFields.includes(field) &&
          item[field as keyof EmploymentFormItem] != null &&
          item[field as keyof EmploymentFormItem] !== "",
      );

      if (isNotEmpty) {
        // Add excluded fields and format for backend submission
        const backendItem = {
          ...item,
          creditRequestId: credit.id,
          partner: partner,
          numOfSalariesPerYear: item.numOfSalariesPerYear
            ? Number(item.numOfSalariesPerYear)
            : undefined,
          iban: item.iban ? IbanUtils.cleanIban(item.iban) : undefined,
        };
        tempArray.push(backendItem as any);
      }
    });
    form.setFieldValue(propertyName, tempArray as BankCreditRequestEmploymentResponse[]);
    setLocalVisibleFields(newVisibleFields);
  }, 300);

  const employmentKeys = Object.keys(employmentForm.getValues());

  const handleAdditionalEmploymentChange = (hasEmployment: boolean) => {
    setHasAdditionalEmployment(hasEmployment);

    if (hasEmployment) {
      if (employmentForm.getValues().length < 2) {
        AppUtils.addNewObject(employmentForm, tempEmploymentObject);
      }
      return;
    }

    const [mainEmployment] = employmentForm.getValues();
    employmentForm.setInitialValues(mainEmployment ? [mainEmployment] : [tempEmploymentObject]);
    employmentForm.setValues(mainEmployment ? [mainEmployment] : [tempEmploymentObject]);

    let mainEmploymentRetained = false;
    form.setFieldValue(
      propertyName,
      (form.getValues()[propertyName] || []).filter(
        (employment: BankCreditRequestEmploymentResponse) => {
          if (employment.partner !== partner) {
            return true;
          }

          if (mainEmploymentRetained) {
            return false;
          }

          mainEmploymentRetained = true;
          return true;
        },
      ),
    );
  };

  return (
    <Box pt={"xs"} mt={"sm"}>
      {employmentKeys.map((key: any, index: number) => {
        if (index > 0 && !hasAdditionalEmployment) {
          return null;
        }

        const employment = employmentForm.getValues()[index];
        const professionalSituation = employment?.professionalSituation;
        const isNumOfSalariesAutoSet = getNumOfSalariesPerYear(professionalSituation) !== undefined;

        const isRequesterPaysSalaryVisible = localVisibleFields.includes(
          `employments.${index}.requesterPaysSalary`,
        );
        const isThirteenthSalaryVisible = localVisibleFields.includes(
          `employments.${index}.thirteenthSalary`,
        );
        const isThirteenthPensionVisible = localVisibleFields.includes(
          `employments.${index}.thirteenthPension`,
        );
        const isSocialHelpVisible = localVisibleFields.includes(`employments.${index}.socialHelp`);
        const isEmploymentTypeVisible = localVisibleFields.includes(
          `employments.${index}.employmentType`,
        );
        const isContractStartDateVisible = localVisibleFields.includes(
          `employments.${index}.contractStartDate`,
        );
        const isContractEndDateVisible = localVisibleFields.includes(
          `employments.${index}.contractEndDate`,
        );
        const isSalaryStructureVisible = localVisibleFields.includes(
          `employments.${index}.salaryStructure`,
        );
        const isEmployerFieldsVisible = localVisibleFields.includes(
          `employments.${index}.employerName`,
        );
        const isSickLeaveReturnDateVisible = localVisibleFields.includes(
          `employments.${index}.sickLeaveReturnDate`,
        );

        const hasAnyFieldVisible =
          isRequesterPaysSalaryVisible ||
          isThirteenthSalaryVisible ||
          isThirteenthPensionVisible ||
          isSocialHelpVisible ||
          isEmploymentTypeVisible ||
          isContractStartDateVisible ||
          isContractEndDateVisible ||
          isSalaryStructureVisible ||
          isSickLeaveReturnDateVisible;

        const isTariffFieldVisible = localVisibleFields.includes(
          `employments.${index}.quellensteuerMatchesTariff`,
        );

        return (
          <Box key={key} mt={index > 0 ? "md" : undefined}>
            <SectionTitle label={getEmploymentSectionTitle(index)} isBankCredit />

            <SimpleGrid cols={4} mt={"sm"}>
              <Select
                label={t(`credit.employment.professionalSituation.label`)}
                data={options["professionalSituation"].map((el) => ({
                  value: el,
                  label: t(`credit.employment.professionalSituation.options.${el}`),
                }))}
                key={employmentForm.key(`${index}.professionalSituation`)}
                {...employmentForm.getInputProps(`${index}.professionalSituation`)}
                onChange={(value) => handleProfessionalSituationChange(value || undefined, index)}
                clearable={true}
                required={true}
                disabled={readOnly}
              />
              <Box /> {/* Empty placeholder */}
              <Box /> {/* Empty placeholder */}
              {index > 0 && !readOnly && (
                <Box style={{ textAlign: "right" }}>
                  <Button
                    variant={"transparent"}
                    size={"compact-sm"}
                    color={"var(--mantine-color-error)"}
                    mb={6}
                    onClick={() =>
                      AppUtils.removeObject(employmentForm, index, tempEmploymentObject)
                    }
                  >
                    <IconTrash size={20} />
                  </Button>
                </Box>
              )}
            </SimpleGrid>

            {hasAnyFieldVisible && (
              <SimpleGrid cols={4} mt={"sm"} mb={"xl"}>
                {isRequesterPaysSalaryVisible && (
                  <YesNoRadioButton
                    label={t(`credit.employment.requesterPaysSalary.label`)}
                    name={`${index}.requesterPaysSalary`}
                    form={employmentForm}
                    disabled={readOnly}
                  />
                )}
                {isThirteenthSalaryVisible && (
                  <YesNoRadioButton
                    label={t(`credit.employment.thirteenthSalary.label`)}
                    name={`${index}.thirteenthSalary`}
                    form={employmentForm}
                    disabled={readOnly}
                  />
                )}
                {isThirteenthPensionVisible && (
                  <YesNoRadioButton
                    label={t(`credit.employment.thirteenthPension.label`)}
                    name={`${index}.thirteenthPension`}
                    form={employmentForm}
                    disabled={readOnly}
                  />
                )}
                {isSocialHelpVisible && (
                  <YesNoRadioButton
                    label={t(`credit.employment.socialHelp.label`)}
                    name={`${index}.socialHelp`}
                    form={employmentForm}
                    disabled={readOnly}
                  />
                )}
                {isEmploymentTypeVisible && (
                  <Select
                    label={t(`credit.employment.employmentType.label`)}
                    data={options["employmentType"].map((el) => ({
                      value: el,
                      label: t(`credit.employment.employmentType.options.${el}`),
                    }))}
                    key={employmentForm.key(`${index}.employmentType`)}
                    value={employmentForm.getValues()[index]?.employmentType}
                    onChange={(value) => handleEmploymentTypeChange(value || undefined, index)}
                    error={employmentTypeErrors[index]}
                    clearable={true}
                    disabled={readOnly}
                  />
                )}
                {isContractStartDateVisible && (
                  <DatePicker
                    label={t(`credit.employment.contractStartDate.label`)}
                    key={employmentForm.key(`${index}.contractStartDate`)}
                    value={employmentForm.getValues()[index]?.contractStartDate}
                    onChange={(value: Date | null) =>
                      handleContractStartDateChange(value || undefined, index)
                    }
                    error={contractStartDateErrors[index]}
                    minDate={isContractEndDateVisible ? moment().subtract(2, "years") : undefined}
                    maxDate={new Date()}
                    disabled={readOnly}
                  />
                )}
                {isContractEndDateVisible && (
                  <MonthPicker
                    label={t(`credit.employment.contractEndDate.label`)}
                    key={employmentForm.key(`${index}.contractEndDate`)}
                    {...employmentForm.getInputProps(`${index}.contractEndDate`)}
                    minDate={new Date()}
                    maxDate={moment().add(6, "months")}
                    disabled={readOnly}
                  />
                )}
                {isSalaryStructureVisible && (
                  <RadioGroup
                    label={t(`bankCredit.salaryStructure.label`)}
                    fieldName={`${index}.salaryStructure`}
                    options={salaryStructureOptions}
                    form={employmentForm}
                    disabled={readOnly}
                    required={false}
                  />
                )}
              </SimpleGrid>
            )}

            <EmploymentPreviousEmployer
              index={index}
              employmentForm={employmentForm}
              localVisibleFields={localVisibleFields}
              readOnly={readOnly}
            />

            <SimpleGrid cols={4}>
              {isEmployerFieldsVisible && (
                <SectionTitle label={t("bankCredit.employer.sectionTitle")} isBankCredit />
              )}
              <SectionTitle label={t("bankCredit.incomeSection.sectionTitle")} isBankCredit />
            </SimpleGrid>

            <Box mt={"md"} mb={40}>
              <SimpleGrid cols={4} spacing="md">
                {isEmployerFieldsVisible && (
                  <Flex gap="md" direction={"column"}>
                    <TextInput
                      label={t("bankCredit.employerName.label")}
                      key={employmentForm.key(`${index}.employerName`)}
                      {...employmentForm.getInputProps(`${index}.employerName`)}
                      required={true}
                      disabled={readOnly}
                      mb={isTariffFieldVisible ? 13 : 0} //hack to align with quellensteuerMatchesTariff field
                    />
                    <StreetAndHouseNumber
                      form={employmentForm}
                      streetFieldName={`${index}.employerStreet`}
                      houseNumberFieldName={`${index}.employerHouseNumber`}
                      disabled={readOnly}
                    />
                    <PostalAndCity
                      form={employmentForm}
                      postalFieldName={`${index}.employerPostal`}
                      cityFieldName={`${index}.employerCity`}
                      disabled={readOnly}
                      label={t("bankCredit.employerPostalAndCity.label")}
                    />
                    <PhoneNumberInput
                      form={employmentForm}
                      fieldName={`${index}.employerPhoneNumber`}
                      label={t("bankCredit.employerPhoneNumber.label")}
                      disabled={readOnly}
                      required={true}
                    />
                    <Box pl={2}>
                      <Flex mt="xs" direction={"column"} gap={4}>
                        <Text size="sm" c="dimmed">
                          {`${t("bankCredit.distance.distanceFromHome")}: ${employmentForm.getValues()[index]?.travelingDistance?.toFixed(2) ?? "X"}km`}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {`${t("bankCredit.distance.travelExpenseBN")}: ${employmentForm.getValues()[index]?.travelExpensesBn?.toFixed(2) ?? "X"} CHF`}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {`${t("bankCredit.distance.travelExpenseCB")}: ${employmentForm.getValues()[index]?.travelExpensesCb?.toFixed(2) ?? "X"} CHF`}
                        </Text>
                      </Flex>
                      <Button
                        variant="transparent"
                        onClick={() => handleCalculateDistance(index)}
                        disabled={readOnly || calculatingDistance[index]}
                        fullWidth={false}
                        styles={{
                          label: { textDecoration: "underline" },
                          root: { padding: 0, backgroundColor: "transparent" },
                        }}
                      >
                        {calculatingDistance[index]
                          ? t("bankCredit.distance.calculating")
                          : t("bankCredit.distance.calculate")}
                      </Button>
                      {distanceError[index] && (
                        <Alert
                          icon={<IconAlertCircle size={16} />}
                          color="red"
                          mt="xs"
                          p={"xs"}
                          variant="light"
                        >
                          {distanceError[index]}
                        </Alert>
                      )}
                    </Box>
                  </Flex>
                )}

                <Box style={{ gridColumn: `span ${isEmployerFieldsVisible ? 3 : 4}` }}>
                  <SimpleGrid cols={isEmployerFieldsVisible ? 3 : 4} spacing="md">
                    <NumberInput
                      label={t(`credit.employment.salary.label`)}
                      key={employmentForm.key(`${index}.salary`)}
                      {...employmentForm.getInputProps(`${index}.salary`)}
                      min={0}
                      required={true}
                      {...DECIMAL_PROPS}
                      disabled={readOnly}
                    />
                    <RadioGroup
                      label={t(`bankCredit.numOfSalariesPerYear.label`)}
                      fieldName={`${index}.numOfSalariesPerYear`}
                      options={numOfSalariesPerYearOptions}
                      form={employmentForm}
                      disabled={readOnly || isNumOfSalariesAutoSet}
                      required={false}
                    />
                    {isTariffFieldVisible && (
                      <Box>
                        <RadioGroup
                          label={t(`bankCredit.quellensteuerMatchesTariff.label`)}
                          fieldName={`${index}.quellensteuerMatchesTariff`}
                          options={quellensteuerOptions}
                          form={employmentForm}
                          disabled={readOnly}
                          required={false}
                        />
                        <Text size="sm" c="dimmed" mt={2}>
                          {`Quellensteuer: ${quellensteuerTariff}`}
                        </Text>
                      </Box>
                    )}
                    <YesNoRadioButton
                      label={t(`bankCredit.bonusReceived.label`)}
                      name={`${index}.bonusReceived`}
                      form={employmentForm}
                      disabled={readOnly}
                    />
                    {localVisibleFields.includes(`employments.${index}.bonusAmount`) && (
                      <NumberInput
                        label={t(`bankCredit.bonusAmount.label`)}
                        key={employmentForm.key(`${index}.bonusAmount`)}
                        {...employmentForm.getInputProps(`${index}.bonusAmount`)}
                        min={0}
                        {...DECIMAL_PROPS}
                        disabled={readOnly}
                      />
                    )}
                    {localVisibleFields.includes(`employments.${index}.bonusType`) && (
                      <RadioGroup
                        label={t(`bankCredit.bonusType.label`)}
                        fieldName={`${index}.bonusType`}
                        options={bonusTypeOptions}
                        form={employmentForm}
                        disabled={readOnly}
                        required={false}
                      />
                    )}
                    {localVisibleFields.includes(`employments.${index}.sickLeaveOnPaylist`) && (
                      <YesNoRadioButton
                        label={t(`bankCredit.sickLeaveOnPaylist.label`)}
                        name={`${index}.sickLeaveOnPaylist`}
                        form={employmentForm}
                        disabled={readOnly}
                      />
                    )}
                    {isSickLeaveReturnDateVisible && (
                      <DatePicker
                        label={t(`bankCredit.sickLeaveReturnDate.label`)}
                        key={employmentForm.key(`${index}.sickLeaveReturnDate`)}
                        value={employmentForm.getValues()[index]?.sickLeaveReturnDate}
                        onChange={(value: Date | null) =>
                          employmentForm.setFieldValue(
                            `${index}.sickLeaveReturnDate`,
                            value || undefined,
                          )
                        }
                        disabled={readOnly}
                      />
                    )}
                    {localVisibleFields.includes(
                      `employments.${index}.childAllowanceForOlderChildren`,
                    ) && (
                      <NumberInput
                        label={t(`bankCredit.childAllowanceForOlderChildren.label`)}
                        key={employmentForm.key(`${index}.childAllowanceForOlderChildren`)}
                        {...employmentForm.getInputProps(`${index}.childAllowanceForOlderChildren`)}
                        min={0}
                        {...DECIMAL_PROPS}
                        disabled={readOnly}
                      />
                    )}
                    {localVisibleFields.includes(
                      `employments.${index}.childAllowanceForYoungerChildren`,
                    ) && (
                      <NumberInput
                        label={t(`bankCredit.childAllowanceForYoungerChildren.label`)}
                        key={employmentForm.key(`${index}.childAllowanceForYoungerChildren`)}
                        {...employmentForm.getInputProps(
                          `${index}.childAllowanceForYoungerChildren`,
                        )}
                        min={0}
                        {...DECIMAL_PROPS}
                        disabled={readOnly}
                      />
                    )}
                    {localVisibleFields.includes(`employments.${index}.childAllowanceMatches`) && (
                      <Box>
                        <YesNoRadioButton
                          label={t(`bankCredit.childAllowanceMatches.label`)}
                          name={`${index}.childAllowanceMatches`}
                          form={employmentForm}
                          disabled={readOnly}
                        />
                        <Text size="sm" c="dimmed" mt="xs">
                          {t("bankCredit.childAllowanceMatches.childrenCount", getChildrenCounts())}
                        </Text>
                      </Box>
                    )}
                    <NumberInput
                      label={t(`bankCredit.mealAllowance.label`)}
                      key={employmentForm.key(`${index}.mealAllowance`)}
                      {...employmentForm.getInputProps(`${index}.mealAllowance`)}
                      min={150}
                      {...DECIMAL_PROPS}
                      disabled={readOnly}
                    />
                    <NumberInput
                      label={t(`bankCredit.employerLoanAmount.label`)}
                      key={employmentForm.key(`${index}.employerLoanAmount`)}
                      {...employmentForm.getInputProps(`${index}.employerLoanAmount`)}
                      min={0}
                      {...DECIMAL_PROPS}
                      disabled={readOnly}
                    />
                    {localVisibleFields.includes(`employments.${index}.employerLoanPaidOut`) && (
                      <MonthPicker
                        label={t(`bankCredit.employerLoanPaidOut.label`)}
                        key={employmentForm.key(`${index}.employerLoanPaidOut`)}
                        {...employmentForm.getInputProps(`${index}.employerLoanPaidOut`)}
                        minDate={new Date()}
                        disabled={readOnly}
                      />
                    )}
                    <Select
                      label={t(`bankCredit.travelingToWorkBn.label`)}
                      data={getFilteredTravelOptions(index, travelingToWorkBnOptions)}
                      key={employmentForm.key(`${index}.travelingToWorkBn`)}
                      value={employmentForm.getValues()[index]?.travelingToWorkBn}
                      onChange={(value) =>
                        handleTravelTypeChange(value as TravelingType, index, "travelingToWorkBn")
                      }
                      clearable={true}
                      disabled={readOnly}
                    />
                    <Select
                      label={t(`bankCredit.travelingToWorkCb.label`)}
                      data={getFilteredTravelOptions(index, travelingToWorkCbOptions)}
                      key={employmentForm.key(`${index}.travelingToWorkCb`)}
                      value={employmentForm.getValues()[index]?.travelingToWorkCb}
                      onChange={(value) =>
                        handleTravelTypeChange(value as TravelingType, index, "travelingToWorkCb")
                      }
                      clearable={true}
                      disabled={readOnly}
                    />
                    {localVisibleFields.includes(`employments.${index}.parkingOnPaylist`) && (
                      <YesNoRadioButton
                        label={t(`bankCredit.parkingOnPaylist.label`)}
                        name={`${index}.parkingOnPaylist`}
                        form={employmentForm}
                        disabled={readOnly}
                      />
                    )}
                    <YesNoRadioButton
                      label={t(`bankCredit.ibanOnPaylist.label`)}
                      name={`${index}.ibanOnPaylist`}
                      form={employmentForm}
                      disabled={readOnly}
                    />
                    {localVisibleFields.includes(`employments.${index}.iban`) && (
                      <TextInput
                        label="IBAN"
                        key={`iban-${key}`}
                        value={employmentForm.getValues()[index]?.iban || ""}
                        onChange={(event) => handleIbanChange(index, event.currentTarget.value)}
                        maxLength={26}
                        placeholder="XX00 0000 0000 0000 0000 0"
                        disabled={readOnly}
                        error={employmentForm.errors[`${index}.iban`]}
                      />
                    )}
                    <Select
                      label={t(`credit.bank.label`)}
                      data={bankOptions}
                      key={employmentForm.key(`${index}.bank`)}
                      {...employmentForm.getInputProps(`${index}.bank`)}
                      clearable={true}
                      disabled={readOnly}
                    />
                  </SimpleGrid>
                </Box>
              </SimpleGrid>
            </Box>

            {index === 0 && (
              <SimpleGrid cols={4} mt="md" pt="md">
                <YesNoRadioButton
                  label={additionalEmploymentLabel}
                  name={partner ? "hasPartnerAdditionalEmployment" : "hasAdditionalEmployment"}
                  checked={hasAdditionalEmployment}
                  onChange={handleAdditionalEmploymentChange}
                  disabled={readOnly}
                />
              </SimpleGrid>
            )}
          </Box>
        );
      })}
      {hasAdditionalEmployment && !readOnly && (
        <Button
          onClick={() => AppUtils.addNewObject(employmentForm, tempEmploymentObject)}
          size="compact-xs"
          variant="transparent"
          mt="md"
        >
          <>
            <Box mr={4}>
              <IconPlus size={16} />
            </Box>
            {t("credit.employment.add")}
          </>
        </Button>
      )}
      <SimpleGrid cols={4} mt="md" pt="md">
        <YesNoRadioButton
          label={additionalIncomeLabel}
          name={partner ? "hasPartnerAdditionalIncome" : "hasAdditionalIncome"}
          checked={hasAdditionalIncome}
          onChange={(hasAdditional) => {
            onAdditionalIncomeChange(hasAdditional, partner);
          }}
          disabled={readOnly}
        />
      </SimpleGrid>
    </Box>
  );
};

export default BankCreditRequestEmployment;
